import React, { useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import { CounterpartyListRow } from '@/types/counterparty.types';
import { Spinner } from '@/components/ui/Spinner';
import { ColumnHeader } from '@/features/inventory/components/helpers/ColumnHeader';
import { useInfiniteScrollLoader } from '@/hooks/common';

interface CounterpartyTableProps {
  data: CounterpartyListRow[];
  columns: ColumnDef<CounterpartyListRow>[];
  isLoading?: boolean;
  /**
   * The read failed AND nothing is loaded — a failed refresh over cached rows
   * must not reach here, or the branch below blanks a populated table.
   */
  isError?: boolean;
  /** Windowed paging — same contract as TradesTable. */
  onLoadMore?: () => Promise<unknown>;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /** The last fetch's error, UNgated — unlike `isError`, which the caller
   *  suppresses over cached rows. Without it the loader auto-fills a dead endpoint. */
  error?: unknown;
  /** Identity of the current search/facets; resets the auto-fill budget. */
  loaderResetKey?: unknown;
}

export const CounterpartyTable: React.FC<CounterpartyTableProps> = ({
  data,
  columns,
  isLoading,
  isError,
  error,
  onLoadMore,
  hasNextPage = false,
  isFetchingNextPage = false,
  loaderResetKey,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Rows render in the server's order. Do NOT re-sort here: against a windowed
  // read each arriving page would reshuffle the whole table (LR-661).
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useInfiniteScrollLoader({
    scrollRef,
    hasNextPage,
    isFetchingNextPage,
    error,
    onLoadMore,
    rowCount: data.length,
    resetKey: loaderResetKey,
  });

  if (isLoading) {
    return <Spinner message="Loading counterparties..." />;
  }

  // Same reasoning as CustodianTable: rendering the affirmative "No
  // counterparties found." over a failed read tells the operator to create a
  // record that may already exist (LR-572).
  if (isError) {
    return (
      <div
        role="alert"
        className="flex flex-1 items-center justify-center p-10 text-sm text-red-700"
      >
        Couldn&apos;t load counterparties — this is a failed request, not an empty register.
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="flex flex-1 flex-col overflow-auto min-h-0">
      <div className="flex flex-1 flex-col" style={{ minWidth: 'max-content' }}>
        {/* Table Header - Styled exactly like TradesTable */}
        <div className="sticky top-0 z-10 flex items-center border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
          {columns.map((column: { accessorKey?: string; id?: string; size?: number; header?: unknown }, colIndex: number) => {
            const columnId = column.accessorKey || column.id || '';
            const width = column.size || 150;
            const headerText = typeof column.header === 'string' ? column.header : '';

            // Mirror the body cell's fixed-width + mx-1.5 box so the header and
            // the data columns stay aligned. ColumnHeader is flex-1 internally, so
            // it fills this fixed-width wrapper instead of equal-splitting the row.
            return (
              <div
                key={colIndex}
                className="mx-1.5 flex min-w-0"
                style={{ width: `${width}px`, minWidth: `${width}px` }}
              >
                <ColumnHeader
                  columnId={columnId}
                  headerText={headerText}
                  width={width}
                  sortState={undefined}
                  onSort={() => {}} // Sorting not implemented for counterparties yet
                  isLoading={!!isLoading}
                  hasError={false}
                  hoveredColumn={null}
                  onHover={() => {}}
                  align="left"
                />
              </div>
            );
          })}
        </div>

        {/* Table Body */}
        <div className="flex-1">
          {table.getRowModel().rows.map((row) => (
            <div key={row.id} className="flex items-center border-b border-gray-200 px-4 py-2 hover:bg-gray-50 transition-colors">
              {row.getVisibleCells().map((cell) => {
                const width = cell.column.columnDef.size || 150;
                return (
                  <div
                    key={cell.id}
                    className="mx-1.5 text-sm text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap"
                    style={{ width: `${width}px`, minWidth: `${width}px` }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                );
              })}
            </div>
          ))}
          {data.length === 0 && (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              No counterparties found.
            </div>
          )}
          {isFetchingNextPage && (
            <div
              role="status"
              className="flex items-center justify-center py-3 text-sm text-gray-500"
            >
              Loading more…
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
